import React, { useState } from 'react';
import { MODULES } from '../../data/modules';

const moduleNames = Object.fromEntries(MODULES.map(m => [m.id, m.name]));

export default function StepReview({ config, onBack, onGenerate, isGenerating, onImport, onExport }) {
  const [importText, setImportText] = useState('');
  const [showImport, setShowImport] = useState(false);

  const sections = [];
  if (config.selectedModules.includes('internet') && config.internet) sections.push({ name: '🌐 การเชื่อมต่ออินเทอร์เน็ต', data: config.internet });
  if (config.selectedModules.includes('vlans') && config.vlans.length > 0) sections.push({ name: '🔀 VLAN', data: config.vlans });
  if (config.selectedModules.includes('dhcp') && config.dhcpServers.length > 0) sections.push({ name: '📡 DHCP Server', data: config.dhcpServers });
  if (config.selectedModules.includes('firewall') && config.firewall) sections.push({ name: '🛡️ ไฟร์วอลล์', data: config.firewall });
  if (config.selectedModules.includes('portforward') && config.portForwards.length > 0) sections.push({ name: '🔌 เปิดพอร์ต', data: config.portForwards });
  if (config.selectedModules.includes('hotspot') && config.hotspot) sections.push({ name: '📶 Hotspot', data: config.hotspot });
  if (config.selectedModules.includes('loadbalance') && config.loadBalance) sections.push({ name: '⚖️ โหลดบาลานซ์', data: config.loadBalance });
  if (config.selectedModules.includes('qos') && config.qos) sections.push({ name: '🚦 QoS', data: config.qos });
  if (config.selectedModules.includes('timentp') && config.timeNtp) sections.push({ name: '🕐 เวลาและ NTP', data: config.timeNtp });
  if (config.selectedModules.includes('logging') && config.logging) sections.push({ name: '📝 ระบบ Log', data: config.logging });
  if (config.selectedModules.includes('security') && config.security) sections.push({ name: '🔒 ความปลอดภัย', data: config.security });
  if (config.selectedModules.includes('vpn') && config.vpn) sections.push({ name: '🔐 VPN Server', data: config.vpn });
  if (config.selectedModules.includes('portforward') && config.portForwards.length > 0) sections.push({ name: '🔌 เปิดพอร์ต', data: config.portForwards });
  if (config.selectedModules.includes('websiteblock') && config.websiteBlock) sections.push({ name: '🚫 บล็อกเว็บไซต์', data: config.websiteBlock });

  const handleImport = () => {
    onImport(importText);
    setShowImport(false);
    setImportText('');
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">✅ ตรวจสอบและสร้างสคริปต์</h2>
        <p className="text-gray-500 dark:text-gray-400">ตรวจสอบการตั้งค่าทั้งหมดก่อนสร้างสคริปต์ MikroTik</p>
      </div>

      {/* Import/Export */}
      <div className="flex gap-2 mb-6">
        <button onClick={onExport} className="btn-secondary text-sm flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          บันทึก JSON
        </button>
        <button onClick={() => setShowImport(!showImport)} className="btn-secondary text-sm flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
          นำเข้า JSON
        </button>
      </div>

      {showImport && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl animate-fadeIn">
          <textarea
            value={importText}
            onChange={e => setImportText(e.target.value)}
            className="input-field font-mono text-xs h-32"
            placeholder='วาง JSON config ที่นี่...'
          />
          <button onClick={handleImport} className="btn-primary text-sm mt-2">นำเข้า</button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="WAN" value={config.internet?.wans?.length || 0} icon="🌐" color="blue" />
        <StatCard label="VLAN" value={config.vlans?.length || 0} icon="🔀" color="purple" />
        <StatCard label="DHCP" value={config.dhcpServers?.length || 0} icon="📡" color="green" />
        <StatCard label="Port Fwd" value={config.portForwards?.length || 0} icon="🔌" color="orange" />
      </div>

      {sections.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <div className="text-4xl mb-3">📭</div>
          <p className="text-lg font-medium">ยังไม่ได้ตั้งค่าโมดูลใดๆ</p>
          <p className="text-sm mt-1">ย้อนกลับและเลือกหัวข้อที่ต้องการตั้งค่า</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sections.map((section, i) => (
            <div key={i} className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-100 dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">{section.name}</h3>
              <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                {JSON.stringify(section.data, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button
          onClick={onGenerate}
          disabled={isGenerating || sections.length === 0}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isGenerating ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              กำลังสร้างสคริปต์...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              สร้างสคริปต์
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  const colorMap = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
  };
  return (
    <div className={`p-3 rounded-xl text-center ${colorMap[color] || colorMap.blue}`}>
      <div className="text-xl">{icon}</div>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-[10px] font-medium opacity-70">{label}</div>
    </div>
  );
}
